/**
 * The phone calendar's head — Aaron, Oct 25, phone note 3.1, Outlook's:
 *
 * - the month (*October 2026* ▾) and **Today**;
 * - the weekday initials, fixed at the top;
 * - the week's dates beneath — today ringed, the selected day filled, days
 *   gone a shade greyer (§6.1), days off at half strength, and under each the
 *   forecast (today and the days ahead, §6.9) or a dot for each thing on it,
 *   up to three; a swipe sideways is the week before or after (pointer
 *   events, decided by the axis the finger sets off on, so it never fights
 *   the grid's scrolling below);
 * - **tap the month** and it grows into a month calendar that scrolls month
 *   after month; a date tapped there is that day, and the strip folds back to
 *   its week. There is no pull-down and no handle any more (Aaron, Oct 27,
 *   notes 1.1.3 and 1.2.1: "it stumbles a bit … let's get rid of the drag
 *   bar"), and the strip is tighter — the dates a little smaller and closer to
 *   the sky under them, every one still a finger's height.
 *
 * In 3 days it is the same head spread over the three columns (note 3.4: "retain
 * the same header, just spread out more to do three days instead of 7"); a day
 * tapped there becomes the first of the three and the view stays 3 days — only
 * the view button changes the view (Oct 27, answer 5). With the month open the
 * three days shown are marked: the first filled as ever, the other two the
 * lighter green the desktop's side panel gives its shown range (§6.1).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  WEEKDAY_LETTERS, addDays, addMonthKey, dayLong, entriesOn, monthCells, monthKeyOf, monthTitle, weekdayOf,
  type DayForecast, type PhoneCalendarView, type ShowState,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { Forecast } from './blocks.js'
import { PAST_TINT } from './look.js'
import { axisOf, stripDays, stripStep, swipeOf } from './phone-time.js'
import { useEndless } from './use-endless.js'
import { useCalendarMonths } from './use-months.js'

/** What the loaded days say about one: its forecast, how many things are on it, whether it is worked. */
export interface StripDay {
  weather: DayForecast | null
  count: number
  off: boolean
}

/** Where the columns start and end under a 3-day head, so each day sits over its own. */
export interface StripInset { left: number; right: number }

/** How many months the picker keeps either side of where it opened; it grows as it is scrolled. */
const MONTHS_EACH_WAY = 12
const MONTHS_MOST = 72

/**
 * A finger on the strip: sideways it follows and swipes; up or down it does
 * nothing (the month opens from its button — Oct 27, note 1.1.3); a tap is
 * left to the button under it, and a gesture's own click is swallowed.
 */
function useStripGesture({ onSwipe }: { onSwipe: (direction: 1 | -1) => void }) {
  const [shift, setShift] = useState(0)
  const swallow = useRef(false)
  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    const x0 = event.clientX
    const y0 = event.clientY
    let axis: 'x' | 'y' | null = null
    const move = (next: PointerEvent) => {
      const dx = next.clientX - x0
      const dy = next.clientY - y0
      axis ??= axisOf(dx, dy)
      if (axis === 'x') setShift(dx)
    }
    const up = (ended: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      setShift(0)
      if (!axis) return
      swallow.current = true
      window.setTimeout(() => { swallow.current = false }, 0)
      if (ended.type === 'pointercancel' || axis !== 'x') return
      const direction = swipeOf(ended.clientX - x0, ended.clientY - y0)
      if (direction) onSwipe(direction)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }
  /* A swipe that ended over a date is not a tap on it. */
  const onClickCapture = (event: React.MouseEvent) => {
    if (!swallow.current) return
    event.preventDefault()
    event.stopPropagation()
  }
  return { shift, onPointerDown, onClickCapture }
}

export function PhoneStrip({
  view, date, today, people, show, dayOf, inset, onDate, onToday,
}: {
  view: Exclude<PhoneCalendarView, 'month'>
  date: string
  today: string
  /** The people shown, for the month's dots. */
  people: readonly string[]
  show: ShowState
  /** What the calendar already loaded says about a day of the strip (nothing for a day it did not load). */
  dayOf: (day: string) => StripDay | null
  /** 3 days: the columns' edges, measured from the screen's. */
  inset?: StripInset
  /** A day tapped or swiped to; in 3 days it is the first of the three. */
  onDate: (day: string) => void
  onToday: () => void
}) {
  const [open, setOpen] = useState(false)
  const three = view === 'three' && !open
  const days = useMemo(() => stripDays(view, date), [view, date])
  const step = stripStep(view)
  const pick = (day: string) => { setOpen(false); onDate(day) }
  const gesture = useStripGesture({ onSwipe: (direction) => onDate(addDays(date, direction * step)) })
  /* 3 days with the month open: the two days after the first are shown too, and marked so. */
  const alsoShown = useMemo(() => (view === 'three' ? [addDays(date, 1), addDays(date, 2)] : []), [view, date])
  const side = three && inset ? { paddingLeft: inset.left, paddingRight: inset.right } : undefined

  return (
    <div data-phone-strip="" className="shrink-0 select-none border-b border-line-faint bg-surface [line-height:normal]">
      <div className="flex h-11 items-center px-2">
        <button type="button" aria-expanded={open} aria-controls="phone-month-picker" onClick={() => setOpen(!open)}
          className="inline-flex h-11 items-center gap-1 rounded-lg px-1.5 text-[15px] font-bold tracking-[-.01em]">
          {monthTitle(monthKeyOf(date))}
          <Glyph name={open ? 'chevronUp' : 'chevronDown'} size={15} stroke={2.2} />
        </button>
        <span className="flex-1" />
        <button type="button" onClick={() => { setOpen(false); onToday() }} className="inline-flex h-11 items-center px-1.5">
          <span className="inline-flex h-8 items-center rounded-lg border border-line bg-surface px-3 text-[12.5px] font-semibold">Today</span>
        </button>
      </div>
      {/* The weekday initials stay at the top, over the week or over the months. */}
      <div aria-hidden data-strip-initials="" className={cn('flex', !three && 'px-2.5')} style={side}>
        {(three ? days.map((day) => WEEKDAY_LETTERS[weekdayOf(day)]!) : [...WEEKDAY_LETTERS]).map((letter, index) => (
          <span key={index} className={cn('flex-1 text-center text-[11px] font-semibold',
            three && days[index] === today ? 'text-accent' : 'text-text-faint')}>{letter}</span>
        ))}
      </div>
      {open ? (
        <MonthPicker date={date} alsoShown={alsoShown} today={today} people={people} show={show} onPick={pick} />
      ) : (
        <div className="touch-none overflow-hidden" onPointerDown={gesture.onPointerDown} onClickCapture={gesture.onClickCapture}>
          <div data-strip-week="" className={cn('flex gap-0.5', !three && 'px-2.5')}
            style={{ ...side, transform: gesture.shift ? `translateX(${gesture.shift}px)` : undefined,
              transition: gesture.shift ? 'none' : 'transform 160ms ease-out' }}>
            {days.map((day) => (
              <StripCell key={day} day={day} date={date} today={today} info={dayOf(day)} onClick={() => onDate(day)} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** One day of the strip: the date in a circle (filled when selected, ringed when today), and under it the sky or the dots. */
function StripCell({ day, date, today, info, onClick }: {
  day: string; date: string; today: string; info: StripDay | null; onClick: () => void
}) {
  const selected = day === date
  const isToday = day === today
  const gone = day < today
  return (
    <button type="button" aria-pressed={selected} aria-label={dayLong(day)} data-day={day} onClick={onClick}
      className={cn('flex min-h-11 min-w-0 flex-1 flex-col items-center gap-px rounded-xl pb-0.5', info?.off && 'opacity-50')}
      style={gone ? { background: PAST_TINT } : undefined}>
      <DateDisc day={day} selected={selected} today={isToday} gone={gone} size={32} />
      <span className="flex h-3 items-center gap-0.5">
        {info?.weather ? (
          <Forecast weather={info.weather} iconOnly iconSize={12} />
        ) : (
          Array.from({ length: Math.min(3, info?.count ?? 0) }, (_, n) => (
            <i key={n} className="inline-block h-1 w-1 rounded-full bg-accent opacity-70" />
          ))
        )}
      </span>
    </button>
  )
}

/**
 * A date's number: filled when it is the day shown, ringed when it is today (Outlook's); in 3 days'
 * month the other two days shown are the lighter green (`accent-soft`, ringed `accent-line` — the
 * design system's selected pair — or `accent` when one of them is today).
 */
function DateDisc({ day, selected, shown = false, today, gone, size }: {
  day: string; selected: boolean; shown?: boolean; today: boolean; gone: boolean; size: number
}) {
  return (
    <span data-today={today ? '' : undefined} data-selected={selected ? '' : undefined} data-shown={shown ? '' : undefined}
      className={cn('tnum inline-flex shrink-0 items-center justify-center rounded-full text-[16px] font-bold',
        selected ? 'bg-accent text-white'
          : shown ? cn('border-[1.5px] bg-accent-soft text-accent-dark', today ? 'border-accent' : 'border-accent-line')
            : today ? 'border-[1.5px] border-accent text-accent-dark'
              : gone ? 'text-text-muted' : 'text-text')}
      style={{ width: size, height: size }}>
      {Number(day.slice(8))}
    </span>
  )
}

/**
 * The month calendar the strip pulls down into: month after month, either
 * way as far as anybody scrolls, opening on the month of the day shown. A dot
 * under a day with something on it, for the months already read (the same
 * months Month and List read — `useCalendarMonths`).
 */
function MonthPicker({ date, alsoShown, today, people, show, onPick }: {
  date: string
  /** 3 days: the second and third days shown, marked the lighter green. */
  alsoShown: readonly string[]
  today: string; people: readonly string[]; show: ShowState; onPick: (day: string) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const opened = monthKeyOf(date)
  const [range, setRange] = useState({ from: addMonthKey(opened, -MONTHS_EACH_WAY), to: addMonthKey(opened, MONTHS_EACH_WAY) })
  const months = useMemo(() => {
    const out: string[] = []
    for (let month = range.from; month <= range.to; month = addMonthKey(month, 1)) out.push(month)
    return out
  }, [range])
  const [top, setTop] = useState(opened)
  const { scrollToKey } = useEndless(box, {
    attr: 'data-month',
    margin: 400,
    onNearTop: () => setRange((was) => {
      const from = addMonthKey(was.from, -MONTHS_EACH_WAY)
      const span = monthsBetween(from, was.to)
      return { from, to: span > MONTHS_MOST ? addMonthKey(from, MONTHS_MOST) : was.to }
    }),
    onNearBottom: () => setRange((was) => {
      const to = addMonthKey(was.to, MONTHS_EACH_WAY)
      const span = monthsBetween(was.from, to)
      return { from: span > MONTHS_MOST ? addMonthKey(to, -MONTHS_MOST) : was.from, to }
    }),
    onTopChange: setTop,
  })
  useLayoutEffect(() => { scrollToKey(opened) }, [opened, scrollToKey])
  /* The dots: the month at the top and the one under it. */
  const { answers } = useCalendarMonths(people, [top, addMonthKey(top, 1)])
  const busy = useMemo(() => {
    const days = new Set<string>()
    for (const answer of answers.values()) {
      for (const day of answer.dates) if (entriesOn(answer, day.date, show).length > 0) days.add(day.date)
    }
    return days
  }, [answers, show])
  useEffect(() => { box.current?.focus({ preventScroll: true }) }, [])

  return (
    <div id="phone-month-picker" ref={box} data-month-picker="" tabIndex={-1} aria-label="Pick a day"
      className="relative overflow-y-auto overscroll-contain outline-none [overflow-anchor:none]"
      style={{ height: 'min(330px, 46dvh)' }}>
      {months.map((month) => (
        <section key={month} data-month={month} aria-label={monthTitle(month)} className="px-2.5 pb-1">
          <h3 className="m-0 px-1.5 pb-1 pt-2.5 text-[13px] font-bold">{monthTitle(month)}</h3>
          {monthCells(month).map((week, row) => (
            <div key={row} className="flex">
              {week.map((day, column) => (day ? (
                <button key={day} type="button" data-day={day} aria-label={dayLong(day)} aria-pressed={day === date}
                  onClick={() => onPick(day)}
                  className="flex h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5">
                  <DateDisc day={day} selected={day === date} shown={alsoShown.includes(day)} today={day === today}
                    gone={day < today} size={32} />
                  <i className={cn('block h-1 w-1 rounded-full', busy.has(day) ? 'bg-accent opacity-70' : 'bg-transparent')} />
                </button>
              ) : <span key={`blank-${column}`} className="h-11 flex-1" />))}
            </div>
          ))}
        </section>
      ))}
    </div>
  )
}

const monthsBetween = (from: string, to: string): number =>
  (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 + Number(to.slice(5, 7)) - Number(from.slice(5, 7))
